'use client';
import React from 'react';
import AppLayout from '@/components/AppLayout';
import { Globe, AlertTriangle, TrendingUp, Info, Building2, Activity } from 'lucide-react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';


const instabilityEvents = [
  { id: 'pol-001', region: 'Taiwan Strait', type: 'Military Escalation', severity: 'High', escalationProb: 55, acmeExposure: 'Electronics sourcing — 3 Taiwan-based component suppliers', impact: 'Procurement risk for Q3 electronics components', timeframe: '7–14 days' },
  { id: 'pol-002', region: 'Russia / Ukraine', type: 'Active Conflict', severity: 'High', escalationProb: 42, acmeExposure: 'Energy commodity procurement — indirect exposure', impact: 'Energy cost volatility, sanctions compliance burden', timeframe: 'Ongoing' },
  { id: 'pol-003', region: 'Iran / Gulf', type: 'Sanctions Escalation', severity: 'Critical', escalationProb: 62, acmeExposure: 'Petrochemical supply chain — 2 Tier-1 suppliers', impact: 'Immediate supplier screening required', timeframe: 'Immediate' },
  { id: 'pol-004', region: 'Sudan / NE Africa', type: 'Civil Conflict', severity: 'Medium', escalationProb: 33, acmeExposure: 'NGO and logistics corridor — low direct exposure', impact: 'Humanitarian logistics disruption', timeframe: '30+ days' },
  { id: 'pol-005', region: 'South China Sea', type: 'Territorial Dispute', severity: 'Medium', escalationProb: 28, acmeExposure: 'APAC maritime routes — monitoring active', impact: 'Potential shipping lane disruption', timeframe: '30+ days' },
];

const severityColor: Record<string, string> = {
  Critical: 'text-danger bg-danger/10',
  High: 'text-warning bg-warning/10',
  Medium: 'text-info bg-info/10',
};

export default function PoliticalRiskPage() {
  return (
    <AppLayout activeRoute="/political-risk">
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Political & Regional Instability</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Geopolitical volatility, conflict escalation, and regional risk scoring</p>
          </div>
          <Link href="/region-monitor" className="flex items-center gap-2 text-sm bg-primary text-primary-foreground font-medium rounded-md px-4 py-2 hover:opacity-90 transition-all">
            Region Monitor
          </Link>
        </div>

        {/* How teams use this */}
        <div className="card-elevated rounded-xl border border-orange-500/20 bg-orange-500/5 p-4">
          <div className="flex items-start gap-3">
            <Info size={15} className="text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-orange-400 uppercase tracking-wide mb-1">How teams use this</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Risk and strategy teams use geopolitical instability scoring to stress-test operational footprints, brief executive leadership on escalation scenarios, adjust regional procurement strategies, and generate board-level country risk assessments with AI-modelled escalation probabilities.
              </p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Political Risk Score', value: '71', unit: '/ 100', icon: Activity, color: 'text-warning', sub: 'Elevated — 5 active hotspots' },
            { label: 'Active Conflict Zones', value: '3', unit: 'regions', icon: AlertTriangle, color: 'text-danger', sub: 'Taiwan, Russia/Ukraine, Iran/Gulf' },
            { label: 'Avg Escalation Prob', value: '44%', unit: '', icon: TrendingUp, color: 'text-warning', sub: 'Across monitored regions' },
            { label: 'Acme Corp Exposure', value: 'High', unit: '', icon: Building2, color: 'text-warning', sub: '3 regions with direct exposure' },
          ].map(({ label, value, unit, icon: Icon, color, sub }) => (
            <div key={label} className="card-elevated rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className={color} />
                <p className="text-xs text-muted-foreground">{label}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <p className={`text-2xl font-bold font-tabular ${color}`}>{value}</p>
                {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
              </div>
              <p className="text-2xs text-muted-foreground">{sub}</p>
            </div>
          ))}
        </div>

        {/* Events Table */}
        <div className="card-elevated rounded-xl border border-border overflow-hidden">
          <div className="p-4 border-b border-border">
            <p className="text-sm font-semibold text-foreground">Active Instability Events</p>
            <p className="text-xs text-muted-foreground mt-0.5">Ranked by escalation probability and Acme Corp exposure</p>
          </div>
          <div className="space-y-0 divide-y divide-border">
            {instabilityEvents.map((event) => (
              <div key={event.id} className="p-4 hover:bg-muted/20 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Globe size={14} className="text-primary flex-shrink-0" />
                    <p className="text-sm font-semibold text-foreground">{event.region}</p>
                    <span className={`text-2xs px-1.5 py-0.5 rounded font-semibold ${severityColor[event.severity]}`}>{event.severity}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xs text-muted-foreground">Escalation prob:</span>
                    <span className={`text-xs font-bold font-tabular ${event.escalationProb > 50 ? 'text-danger' : event.escalationProb > 35 ? 'text-warning' : 'text-info'}`}>{event.escalationProb}%</span>
                    <span className="text-2xs text-muted-foreground">· {event.timeframe}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-2">{event.type}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-start gap-1.5">
                    <Building2 size={11} className="text-warning flex-shrink-0 mt-0.5" />
                    <p className="text-2xs text-muted-foreground">{event.acmeExposure}</p>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <TrendingUp size={11} className="text-danger flex-shrink-0 mt-0.5" />
                    <p className="text-2xs text-muted-foreground">{event.impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
